import {
  brandName,
  date,
  enumUrl,
  imageList,
  integer,
  number,
  personName,
  stringList,
  text,
  unitReference,
} from "./emit";
import type { Recipe } from "./types";

/**
 * Como cada propriedade vira JSON-LD, em PT-BR e com a forma que o schema.org
 * espera. Escrita à mão de propósito: nada disso está no vocabulário do
 * schema.org, que publica domínio e alcance mas não diz como chamar a
 * propriedade em português nem que constante acompanha uma oferta.
 *
 * A chave é o caminho da propriedade. Caminho pontuado aninha — o `@type` do
 * objeto aninhado sai de `containers`, em `vocabulary.ts`.
 *
 * Cada propriedade existe **uma vez**, mesmo quando as duas entidades a aceitam:
 * `name` é a mesma receita em Produto e em Post de blog.
 */
export const recipes: Record<string, Recipe> = {
  name: {
    label: "Nome",
    description: "Nome do item, como aparece no site.",
    icon: "Type",
    accepts: ["text", "textarea", "select", "dropdown"],
    emit: text(),
  },
  headline: {
    label: "Título",
    description: "Título da publicação.",
    icon: "Heading",
    accepts: ["text", "textarea"],
    emit: text(),
  },
  description: {
    label: "Descrição",
    description:
      "Descrição do registro. Campo de texto rico entra sem a formatação.",
    icon: "AlignLeft",
    accepts: ["text", "textarea", "rich_text"],
    emit: text(),
  },
  articleBody: {
    label: "Corpo do texto",
    description: "Texto completo da publicação.",
    icon: "FileText",
    accepts: ["textarea", "rich_text"],
    emit: text(),
  },
  image: {
    label: "Imagem",
    description: "Fotos do registro. Capa e galeria podem apontar para cá — as duas somam.",
    icon: "Image",
    accepts: ["image", "gallery", "media"],
    emit: imageList(),
    list: true,
  },
  sku: {
    label: "SKU",
    description: "Código de estoque do veículo, da peça ou do acessório.",
    icon: "Barcode",
    accepts: ["text"],
    emit: text(),
  },
  gtin: {
    label: "GTIN",
    description:
      "Código de barras global do item, com 8, 12, 13 ou 14 dígitos numéricos.",
    icon: "Barcode",
    accepts: ["text"],
    emit: text(),
  },
  mpn: {
    label: "Código do fabricante",
    description: "Código da peça atribuído pelo fabricante.",
    icon: "Hash",
    accepts: ["text"],
    emit: text(),
  },
  productID: {
    label: "Identificador do produto",
    description: "Identificador próprio do registro, quando não é código de estoque nem de barras. Código FIPE entra aqui.",
    icon: "Fingerprint",
    accepts: ["text"],
    emit: text(),
  },
  model: {
    label: "Modelo",
    description: "Modelo do veículo, sem a marca — Onix, Civic, Strada.",
    icon: "CarFront",
    accepts: ["text", "select", "dropdown"],
    emit: text(),
  },
  category: {
    label: "Categoria",
    description: "Classificação dentro do catálogo — segmento, linha ou família.",
    icon: "Layers",
    accepts: ["text", "select", "dropdown"],
    emit: text(),
  },
  articleSection: {
    label: "Editoria",
    description: "Editoria do blog.",
    icon: "Layers",
    accepts: ["text", "select", "dropdown"],
    emit: text(),
  },
  keywords: {
    label: "Itens e características",
    description:
      "Lista de itens que descrevem o registro — opcionais do veículo, itens de série, selos, vantagens da oferta. Aceita lista de conteúdos ou texto separado por vírgula.",
    icon: "Tags",
    accepts: ["text", "array_string"],
    emit: stringList(),
    list: true,
  },
  wordCount: {
    label: "Número de palavras",
    description: "Quantidade de palavras da publicação.",
    icon: "Hash",
    accepts: ["number"],
    emit: integer(),
  },
  color: {
    label: "Cor",
    description: "Cor externa predominante.",
    icon: "Palette",
    accepts: ["text", "select", "dropdown"],
    emit: text(),
  },
  material: {
    label: "Material",
    description: "Material de que a peça ou o acessório é feito.",
    icon: "Box",
    accepts: ["text", "select", "dropdown"],
    emit: text(),
  },
  slogan: {
    label: "Chamada",
    description: "Frase curta de destaque, do tipo chamada promocional.",
    icon: "Megaphone",
    accepts: ["text", "textarea", "rich_text"],
    emit: text(),
  },
  award: {
    label: "Prêmio",
    description: "Prêmio ou reconhecimento recebido.",
    icon: "Award",
    accepts: ["text"],
    emit: text(),
  },
  releaseDate: {
    label: "Data de lançamento",
    description: "Quando o registro passou a ser oferecido.",
    icon: "Calendar",
    accepts: ["date", "datetime", "number", "text"],
    emit: date(),
  },
  productionDate: {
    label: "Ano de fabricação",
    description: "Ano de fabricação.",
    icon: "Calendar",
    accepts: ["number", "text", "date"],
    emit: date(),
  },
  datePublished: {
    label: "Data de publicação",
    description: "Quando a publicação foi feita.",
    icon: "CalendarDays",
    accepts: ["date", "datetime"],
    emit: date({ keepTime: true }),
  },
  dateModified: {
    label: "Data de atualização",
    description: "Última alteração na publicação.",
    icon: "CalendarClock",
    accepts: ["date", "datetime"],
    emit: date({ keepTime: true }),
  },
  itemCondition: {
    label: "Condição do item",
    description: "Se o veículo é novo ou usado. Exige a correspondência de valores logo abaixo.",
    icon: "Sparkles",
    accepts: ["switch", "select", "dropdown"],
    emit: enumUrl({
      new: "NewCondition",
      used: "UsedCondition",
      refurbished: "RefurbishedCondition",
      damaged: "DamagedCondition",
    }),
    suggestions: {
      new: "Novo",
      used: "Usado",
      refurbished: "Recondicionado",
      damaged: "Danificado",
    },
  },

  "brand.name": {
    label: "Marca",
    description: "Marca do veículo. Campo do tipo Marca é resolvido pelo cadastro da plataforma.",
    icon: "BadgeCheck",
    accepts: ["text", "brand_id", "select", "dropdown"],
    emit: brandName(),
  },

  "offers.price": {
    label: "Preço",
    description: "Valor que o cliente paga. Moeda e disponibilidade entram sozinhas.",
    icon: "DollarSign",
    accepts: ["price", "number"],
    emit: number(),
  },
  "offers.validFrom": {
    label: "Oferta válida a partir de",
    description: "Quando a condição de preço passa a valer.",
    icon: "CalendarPlus",
    accepts: ["date", "datetime"],
    emit: date({ keepTime: true }),
  },
  // O Google aceita `validThrough` **ou** `priceValidUntil` para a mesma coisa,
  // nunca as duas. Fica `validThrough`, que faz par com `validFrom` na mesma
  // entidade e cujo range inclui `DateTime` — o de `priceValidUntil` é só `Date`.
  "offers.validThrough": {
    label: "Oferta válida até",
    description: "Até quando a condição de preço vale.",
    icon: "CalendarClock",
    accepts: ["date", "datetime"],
    emit: date({ keepTime: true }),
  },
  "offers.seller": {
    label: "Vendedor",
    description: "Unidade que vende. Aponta para a loja já publicada no site.",
    icon: "Store",
    accepts: ["unit_id"],
    emit: unitReference(),
  },
  "offers.priceSpecification.price": {
    label: "Preço de",
    description:
      "Preço anterior, antes da condição atual. Emitido como preço de tabela, para o buscador distinguir do valor cobrado.",
    icon: "TicketPercent",
    accepts: ["price", "number"],
    emit: number(),
  },

  "author.name": {
    label: "Autor",
    description: "Nome de quem escreveu o texto.",
    icon: "User",
    accepts: ["text", "select", "dropdown"],
    emit: personName(),
  },
  "author.url": {
    label: "Página do autor",
    description:
      "Endereço que identifica o autor de forma única — perfil dele no site ou numa rede.",
    icon: "ExternalLink",
    accepts: ["text"],
    emit: text(),
  },
  "author.image": {
    label: "Foto do autor",
    description: "Imagem de quem escreveu o texto.",
    icon: "UserRound",
    accepts: ["image", "gallery", "media"],
    emit: imageList(),
    list: true,
  },
};
